<template>
  <div
    v-if="visible"
    class="preloader"
    :class="{ 'is-revealing': isRevealing, 'is-centered': isCentered }"
    aria-hidden="true"
  >
    <div class="preloader__content">
      <div class="preloader__spacer" aria-hidden="true"></div>
      <div class="preloader__logo-col">
        <div class="preloader__logo" :class="{ 'is-hidden': logoHidden }" ref="logoRef">
          <span v-for="(char, idx) in 'Juan Lias'.split('')" :key="idx" class="preloader__char">
            {{ char === ' ' ? '\u00A0' : char }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import gsap from 'gsap'

const emit = defineEmits(['done'])

const visible = ref(true)
const isRevealing = ref(false)
const logoHidden = ref(false)
const logoRef = ref(null)
const isCentered = ref(false)

const checkScroll = () => {
  const scrollTop =
    window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0
  isCentered.value = scrollTop > 0
}

let scrollInterval = null

onMounted(async () => {
  checkScroll()

  window.addEventListener('scroll', checkScroll, { passive: true })

  scrollInterval = setInterval(checkScroll, 50)
  setTimeout(() => {
    if (scrollInterval) {
      clearInterval(scrollInterval)
      scrollInterval = null
    }
  }, 2500)

  if (document.fonts && document.fonts.ready) {
    try {
      await Promise.race([
        document.fonts.ready,
        new Promise((resolve) => setTimeout(resolve, 1500)),
      ])
    } catch (err) {
      console.warn('Failed to wait for fonts:', err)
    }
  }

  if (logoRef.value) {
    const chars = logoRef.value.querySelectorAll('.preloader__char')
    gsap.fromTo(
      chars,
      {
        opacity: 0,
        y: 30,
        filter: 'blur(10px)',
        rotateX: -45,
      },
      {
        opacity: 1,
        y: 0,
        filter: 'blur(0px)',
        rotateX: 0,
        stagger: 0.05,
        duration: 1.2,
        ease: 'power4.out',
        delay: 0.1,
      }
    )
  }

  setTimeout(() => {
    isRevealing.value = true
    window.__PRELOADER_DONE__ = true
    window.dispatchEvent(new CustomEvent('preloader:done'))

    if (isCentered.value) {
      logoHidden.value = true
      window.__PRELOADER_ALL_DONE__ = true
      window.dispatchEvent(new CustomEvent('preloader:all-done'))

      setTimeout(() => {
        visible.value = false
        emit('done')
      }, 1000)
    } else {
      setTimeout(() => {
        logoHidden.value = true
        window.__PRELOADER_ALL_DONE__ = true
        window.dispatchEvent(new CustomEvent('preloader:all-done'))
      }, 2000)

      setTimeout(() => {
        visible.value = false
        emit('done')
      }, 3000)
    }
  }, 1200)
})

onUnmounted(() => {
  window.removeEventListener('scroll', checkScroll)
  if (scrollInterval) {
    clearInterval(scrollInterval)
  }
})
</script>

<style scoped>
.preloader {
  position: fixed;
  inset: 0;
  z-index: 99;
  background-color: var(--color-deep-black);
  transition: background-color 1.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.preloader.is-revealing {
  background-color: transparent;
  pointer-events: none;
}

/* scroll=0: grid layout espejo del HeroV6 */
.preloader__content {
  width: 100%;
  display: grid;
  grid-template-rows: 1fr;
  grid-template-columns: 1fr;
  grid-template-areas: 'logo';
  min-height: 100svh;
  padding: 0 1.5rem;
}

.preloader__spacer {
  display: none;
}

.preloader__logo-col {
  grid-area: logo;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding-top: 4rem;
  padding-bottom: 8vh;
}

@media (min-width: 768px) {
  .preloader__content {
    padding: 0 3rem 0 0;
    grid-template-columns: 42% 1fr;
    grid-template-areas: 'spacer logo';
  }

  .preloader__spacer {
    display: block;
    grid-area: spacer;
  }

  .preloader__logo-col {
    padding-top: 6rem;
    padding-left: 0.5rem;
    margin-left: -8rem;
  }
}

@media (min-height: 900px) {
  .preloader__logo-col {
    padding-top: 8rem;
  }
}

@media (min-height: 1100px) {
  .preloader__logo-col {
    padding-top: 10rem;
  }
}

@media (min-width: 1024px) {
  .preloader__content {
    padding: 0 4rem 0 0;
  }

  .preloader__logo-col {
    padding-left: 1rem;
    margin-left: -12rem;
  }
}

@media (min-width: 1280px) {
  .preloader__content {
    grid-template-columns: 40% 1fr;
  }

  .preloader__logo-col {
    margin-left: -16rem;
  }
}

/* scrolled: centrado en pantalla */
.preloader.is-centered .preloader__content {
  height: 100%;
  min-height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.preloader.is-centered .preloader__spacer {
  display: none;
}

.preloader.is-centered .preloader__logo-col {
  padding-top: 0;
  padding-left: 0;
  margin-left: 0;
}

.preloader__logo {
  @apply font-madi italic;
  font-size: clamp(2.5rem, 6vw, 2.8rem);
  letter-spacing: 0.05em;
  color: var(--color-text-primary);
  perspective: 1000px;
}

.preloader__char {
  display: inline-block;
  will-change: transform, opacity, filter;
  opacity: 0;
}

.preloader__logo.is-hidden {
  opacity: 0;
  transform: translateY(-20px);
  filter: blur(10px);
  transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
}
</style>
